import React from "react";
import { ResponsiveContainer, ComposedChart, BarChart, LineChart, Bar, Line, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, ReferenceLine } from "recharts";
import { CalendarPlus, TrendingUp, TrendingDown, Flame, UserPlus } from "lucide-react";
import { Section, Card, Kpi, GenderLegend, Empty, C, axis, tip } from "./parts";

const Growth = ({ g }) => {
    if (g == null) return <span className="text-slate-400">—</span>;
    const up = g >= 0, I = up ? TrendingUp : TrendingDown;
    return <span className={`inline-flex items-center gap-1 ${g === 0 ? "text-slate-600" : up ? "text-emerald-600" : "text-rose-600"}`}><I className="h-4 w-4" />{g > 0 ? "+" : ""}{g}%</span>;
};

const NewJoinerGender = ({ nj }) => {
    const data = [{ name: "Male", value: nj.male, fill: C.male }, { name: "Female", value: nj.female, fill: C.female }, { name: "Not set", value: nj.unset, fill: C.unset }].filter((x) => x.value);
    if (!nj.total) return <Empty>No one joined in the last 12 months.</Empty>;
    const set = nj.male + nj.female;
    return (
        <>
            <div className="h-40">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                    <PieChart>
                        <Pie data={data} dataKey="value" nameKey="name" innerRadius={40} outerRadius={62} paddingAngle={data.length > 1 ? 2 : 0} stroke="none">{data.map((x) => <Cell key={x.name} fill={x.fill} />)}</Pie>
                        <Tooltip {...tip} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className="text-center text-xs text-slate-600" data-testid="hiring-newjoiner-split">
                {nj.total} joiners · {set ? `${Math.round((nj.female * 100) / set)}% female` : "gender not set"}
            </div>
            <div className="flex justify-center mt-1"><GenderLegend /></div>
        </>
    );
};

export const HiringSection = ({ h, byYear }) => (
    <Section no="03" title="Hiring Intelligence" testid="wf-hiring">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label={`Joined in ${h.mom.label}`} value={h.mom.cur} icon={<UserPlus className="h-3 w-3" />} sub={`${h.mom.prev} in ${h.mom.prevLabel}`} testid="hiring-this-month" />
            <Kpi label="Month-on-month growth" value={<Growth g={h.mom.growth} />} icon={<TrendingUp className="h-3 w-3" />} sub={`${h.mom.label} vs ${h.mom.prevLabel}`} testid="hiring-mom" />
            <Kpi label="Joined in last 12 months" value={h.newJoiners.total} icon={<CalendarPlus className="h-3 w-3" />} sub={`${h.newJoiners.male} male · ${h.newJoiners.female} female`} testid="hiring-12m" />
            <Kpi label="Peak hiring months" value={h.peakTop.length ? h.peakTop.join(", ") : "—"} tone="text-amber-700" icon={<Flame className="h-3 w-3 text-amber-500" />} sub="Across all years" testid="hiring-peak" />
        </div>
        <div className="grid lg:grid-cols-3 gap-3">
            <Card title="Monthly joining count" hint="last 12 months · line = MoM growth %" className="lg:col-span-2" testid="hiring-monthly">
                {h.monthly.some((x) => x.total) ? <><div className="h-56">
                    <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                        <ComposedChart maxBarSize={36} data={h.monthly} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
                            <XAxis dataKey="label" {...axis} interval={0} tick={{ ...axis.tick, fontSize: 10 }} />
                            <YAxis yAxisId="n" allowDecimals={false} {...axis} />
                            <YAxis yAxisId="g" orientation="right" {...axis} tickFormatter={(v) => `${v}%`} />
                            <ReferenceLine yAxisId="g" y={0} stroke={C.amber} strokeDasharray="3 3" strokeOpacity={0.5} />
                            <Tooltip {...tip} formatter={(v, n) => (n === "MoM growth" ? [v == null ? "—" : `${v}%`, n] : [v, n])} />
                            <Bar yAxisId="n" dataKey="male" name="Male" stackId="g" fill={C.male} />
                            <Bar yAxisId="n" dataKey="female" name="Female" stackId="g" fill={C.female} />
                            <Bar yAxisId="n" dataKey="unset" name="Not set" stackId="g" fill={C.unset} radius={[3, 3, 0, 0]} />
                            <Line yAxisId="g" dataKey="growth" name="MoM growth" stroke={C.amber} strokeWidth={2} dot={{ r: 2 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
                <GenderLegend /></> : <Empty>No one joined in the last 12 months.</Empty>}
            </Card>
            <Card title="Gender diversity · new joiners" hint="last 12 months" testid="hiring-newjoiners">
                <NewJoinerGender nj={h.newJoiners} />
            </Card>
        </div>
        <div className="grid lg:grid-cols-3 gap-3">
            <Card title="Quarterly joining count" hint="last 8 quarters" testid="hiring-quarterly">
                <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                        <BarChart maxBarSize={48} data={h.quarters} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                            <XAxis dataKey="label" {...axis} tick={{ ...axis.tick, fontSize: 10 }} />
                            <YAxis allowDecimals={false} {...axis} />
                            <Tooltip {...tip} />
                            <Bar dataKey="total" name="Joiners" fill={C.teal} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>
            <Card title="Year-wise joining trend" testid="hiring-yearly">
                {byYear.length ? (
                    <div className="h-44">
                        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                            <LineChart data={byYear} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                                <XAxis dataKey="year" {...axis} />
                                <YAxis allowDecimals={false} {...axis} />
                                <Tooltip {...tip} />
                                <Line dataKey="total" name="Joiners" stroke={C.teal} strokeWidth={2.5} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                ) : <Empty />}
            </Card>
            <Card title="Peak hiring months" hint="all years combined" testid="hiring-peak-chart">
                <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                        <BarChart maxBarSize={48} data={h.peak} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                            <XAxis dataKey="month" {...axis} interval={0} tick={{ ...axis.tick, fontSize: 9 }} />
                            <YAxis allowDecimals={false} {...axis} />
                            <Tooltip {...tip} />
                            <Bar dataKey="total" name="Joiners" radius={[3, 3, 0, 0]}>{h.peak.map((m) => <Cell key={m.month} fill={m.peak ? C.amber : "#E2E8F0"} />)}</Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </div>
    </Section>
);
